import React, { useEffect, useState } from "react";
import BoxComponent from "../../../../../components/Box/BoxComponent";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { CatalogoComplemento, CatalogoOpcao } from "../../../public/CatalogoPage";
import TextFieldComponent from "../../../../../components/TextField/TextFieldComponent";
import LabelComponent from "../../../../../components/Label/LabelComponent";
import ButtonComponent from "../../../../../components/Button/ButtonComponent";
import { formatNumberToDinheiro } from "../../../../../utils/dinheiro";

interface ModalComplementoProps {
    open: boolean;
    onClose: () => void;

    isComplemento: boolean;
    isOpcao: boolean;

    complemento?: CatalogoComplemento | null;
    opcao?: CatalogoOpcao | null;

    handleSend: (data: CatalogoComplemento | CatalogoOpcao) => void;
}

const buttonStyle = {
    width: '119px',
    height: '42px',
    padding: '8px 24px',
    borderRadius: '8px',
    opacity: 1,
    minWidth: 'auto',

    border: '1px solid var(--color-orange-87, #E8DFD5)',

    fontFamily: '"Font 7", sans-serif',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0%',
    textAlign: 'center',
    textTransform: 'none',

    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
    boxSizing: 'border-box',

    // // Efeito de hover básico para o botão não parecer estático
    // '&:hover': {
    //     backgroundColor: 'rgba(232, 223, 213, 0.08)', // Um leve reflexo no tom da borda
    //     border: '1px solid var(--color-orange-87, #E8DFD5)',
    // }
}

const labelStyle = {
    fontFamily: '"Font 7", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0%',
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle'
}

const inputStyle = {
    //width: '400px',
    height: '42px',
    opacity: 1,

    // Customização da borda e inputs internos do MUI
    '& .MuiOutlinedInput-root': {
        height: '42px',
        padding: '8px 16px', // Alinha com seu padding de design
        borderRadius: '8px',

        // Cor da borda em repouso
        '& fieldset': {
            border: '1px solid var(--color-orange-87, #E8DFD5)',
        },
        // Cor da borda ao passar o mouse (hover)
        '&:hover fieldset': {
            borderColor: '#FF8C42', // Usando aquele laranja padrão de ontem para o hover, ajuste se preferir!
        },
        // Cor da borda quando estiver focado (clicado)
        '&.Mui-focused fieldset': {
            borderColor: '#FF8C42',
            borderWidth: '1.5px', // Destaca o foco de forma suave
        },
    },
    // Ajuste do espaçamento interno do texto digitado para caber nos 42px de altura
    '& .MuiInputBase-input': {
        padding: 0,
        boxSizing: 'border-box',
    }
};

const ModalComplemento = (props: ModalComplementoProps) => {
    const [formData, setFormData] = useState<CatalogoComplemento | CatalogoOpcao>();

    const complementoVazio: CatalogoComplemento = {
        nome: "",
        vlrUnitario: 0,
        vlrUnitarioStr: "",
        ativo: true
    };

    const opcaoVazia: CatalogoOpcao = {
        nome: "",
        descricao: "",
        tipo: "",
        ativo: true
    };

    const renderTitle = () => {
        const existsComplemento = props.complemento !== null;
        const existsOpcao = props.opcao !== null;

        if (props.isComplemento) {
            if (existsComplemento) return "Editar complemento";
            else return "Novo complemento";
        } else if (props.isOpcao) {
            if (existsOpcao) return "Editar opção";
            else return "Nova opção";
        }
    }

    useEffect(() => {
        if (props.isComplemento) {
            const complemento: CatalogoComplemento = {
                ...(props.complemento ?? complementoVazio),
                vlrUnitarioStr: formatNumberToDinheiro(
                    props.complemento?.vlrUnitario ?? 0
                )
            };
    
            setFormData(complemento);
        } else {
            setFormData(props.opcao ?? opcaoVazia);
        }
    }, [props.open]);

    const handleChangeFormData = (label: string, value: any) => {
        setFormData({
            ...formData!,
            [label]: value
        })
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();    
        if (!formData) return;    
        props.handleSend(formData);
    };

    return <Dialog
        //fullScreen
        open={props.open}
        onClose={props.onClose}
        //TransitionComponent={Transition}
        PaperProps={{
            component: "form",
            onSubmit: handleSubmit
        }}
    >
        <DialogTitle
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontFamily: '"Font 7", sans-serif',
                fontWeight: 700,
                fontSize: '24px',
                lineHeight: '32px',
                letterSpacing: '0%',
                color: '#2C2C2C',
                // display: 'inline-flex',
                // alignItems: 'center',
                // verticalAlign: 'middle',
            }}
        >
            {renderTitle()}

            <IconButton onClick={props.onClose}>
                X
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2
        }}>
            <BoxComponent component="div">
                <LabelComponent htmlFor="nome" sx={labelStyle}>
                    Nome *
                </LabelComponent>
                <TextFieldComponent
                    id="nome"
                    placeholder="Ex: Bacon Extra, Queijo Adicional..."
                    variant="outlined"
                    value={formData?.nome ?? ""}
                    onChange={(e) => { handleChangeFormData("nome", e.target.value) }}
                    fullWidth
                    sx={inputStyle}
                    required
                />
            </BoxComponent>

            {props.isOpcao && (
                <>
                    <BoxComponent component="div">
                        <LabelComponent htmlFor="descricao" sx={labelStyle}>
                            Descrição *
                        </LabelComponent>
                        <TextFieldComponent
                            id="descricao"
                            placeholder="Ex: Tamanho grande, pequeno.."
                            variant="outlined"
                            value={
                                "descricao" in formData!
                                    ? formData.descricao
                                    : ""
                            }
                            onChange={(e) => { handleChangeFormData("descricao", e.target.value) }}
                            fullWidth
                            sx={inputStyle}
                            //maskDinheiro
                            required
                        />
                    </BoxComponent>

                    <BoxComponent component="div">
                        <LabelComponent htmlFor="tipo" sx={labelStyle}>
                            Tipo *
                        </LabelComponent>
                        <TextFieldComponent
                            id="tipo"
                            placeholder="Ex: tamanho"
                            variant="outlined"
                            value={
                                "tipo" in formData!
                                    ? formData.tipo
                                    : ""
                            }
                            onChange={(e) => { handleChangeFormData("tipo", e.target.value) }}
                            fullWidth
                            sx={inputStyle}
                            //maskDinheiro
                            required
                        />
                    </BoxComponent>
                </>
            )}

            {props.isComplemento && (
                <BoxComponent component="div">
                    <LabelComponent htmlFor="vlrUnitario" sx={labelStyle}>
                        Preço *
                    </LabelComponent>
                    <TextFieldComponent
                        id="vlrUnitario"
                        placeholder=""
                        variant="outlined"
                        value={
                            "vlrUnitarioStr" in formData!
                                ? (formData.vlrUnitarioStr ?? "")
                                : ""
                        }
                        onChange={(e) => { handleChangeFormData("vlrUnitarioStr", e.target.value) }}
                        fullWidth
                        sx={inputStyle}
                        maskDinheiro
                        required
                    />
                </BoxComponent>
            )}
        </DialogContent>
        <DialogActions>
            <ButtonComponent
                id="cancelar"
                variant="outlined"
                type="button"
                sx={{ ...buttonStyle, color: '#2C2C2C' }}
                onClick={props.onClose}
            >
                Cancelar
            </ButtonComponent>
            <ButtonComponent
                id="salvar"
                variant="outlined"
                type="submit"
                sx={{ ...buttonStyle, backgroundColor: "#FF8C42", color: '#FFFFFF' }}
            >
                Salvar
            </ButtonComponent>
        </DialogActions>
    </Dialog>
}

export default ModalComplemento;